import React, { useId } from "react";
import {
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Typography,
    Box,
} from "@mui/material";

interface MiddleDropdownProps {
    title: string;
    body: string | Record<string, string> | [string, string][];
    isObject?: boolean;
    icon?: string;
    isExpanded: boolean;
    customColor?: string;
    onClick: () => void;
    onBodyItemClick?: (key: string) => void;
    hasDataToDownload?: boolean;
}

const MiddleDropdown: React.FC<MiddleDropdownProps> = ({
    title,
    body,
    isObject = false,
    icon,
    isExpanded,
    customColor = '#eef1f7',
    onClick,
    onBodyItemClick,
    hasDataToDownload = true
}) => {
    const contentId = useId();
    const summaryId = useId();

    const renderBody = () => {
        if (!isExpanded) return null;

        if (isObject) {
            if (Array.isArray(body)) {
                return (
                    <>
                        {body.map((item: any, index: number) => (
                            <Typography
                                key={index}
                                sx={{
                                    fontFamily: "Heebo",
                                    fontSize: "14px",
                                    lineHeight: "135%",
                                    textAlign: "right",
                                    color: "#0C3768",
                                    marginBottom: "4px",
                                    paddingRight: "15px",
                                }}
                            >
                                {item}
                            </Typography>
                        ))}
                    </>
                );
            }

            if (typeof body === "object") {
                return (
                    <>
                        {Object.entries(body).map(([key, value], index) => {
                            // ✅ הורדה = קישור
                            if (value === "download") {
                                // אם אין נתונים להורדה - הצג כטקסט לא פעיל
                                if (!hasDataToDownload) {
                                    return (
                                        <Typography
                                            key={`${key}-${index}`}
                                            component="span"
                                            tabIndex={-1}
                                            aria-disabled="true"
                                            sx={{
                                                fontFamily: "Heebo",
                                                fontSize: "14px",
                                                lineHeight: "135%",
                                                textAlign: "right",
                                                marginBottom: "4px",
                                                paddingRight: "15px",
                                                color: "#999999",
                                                cursor: "not-allowed",
                                                textDecoration: "none",
                                            }}
                                        >
                                            {key}
                                        </Typography>
                                    );
                                }

                                // אם יש נתונים - הצג כקישור פעיל
                                return (
                                    <Typography
                                        key={`${key}-${index}`}
                                        component="a"
                                        href="#"
                                        onClick={(e) => {
                                            e.preventDefault();
                                            onBodyItemClick?.(key);
                                        }}
                                        sx={{
                                            fontFamily: "Heebo",
                                            fontSize: "14px",
                                            lineHeight: "135%",
                                            textAlign: "right",
                                            marginBottom: "4px",
                                            paddingRight: "15px",
                                            color: "#1293FE",
                                            cursor: "pointer",
                                            textDecoration: "underline",
                                            '&:focus-visible': {
                                                outline: '2px solid #005fcc',
                                                outlineOffset: '2px',
                                            },
                                        }}
                                    >
                                        {key}
                                    </Typography>
                                );
                            }

                            // טקסט רגיל
                            return (
                                <Typography
                                    key={`${key}-${index}`}
                                    sx={{
                                        fontFamily: "Heebo",
                                        fontSize: "14px",
                                        lineHeight: "135%",
                                        textAlign: "right",
                                        marginBottom: "4px",
                                        paddingRight: "15px",
                                        color: "#0C3768",
                                    }}
                                >
                                    {key}
                                </Typography>
                            );
                        })}
                    </>
                );
            }
        }

        if (typeof body === "string") {
            return (
                <Typography
                    sx={{
                        fontFamily: "Heebo",
                        fontSize: "14px",
                        lineHeight: "135%",
                        textAlign: "justify",
                        paddingRight: "15px",
                        color: "#0C3768",
                    }}
                >
                    {body}
                </Typography>
            );
        }

        return null;
    };

    return (
        <Accordion
            expanded={isExpanded}
            onChange={onClick}
            disableGutters
            sx={{
                border: "3px solid " + customColor,
                borderRadius: "15px !important",
                backgroundColor: customColor,
                minHeight: "45px",
                mb: "6px",
                boxShadow: "none",
            }}
        >
            <AccordionSummary
                id={summaryId}
                aria-controls={contentId}
                aria-expanded={isExpanded}
                sx={{
                    flexDirection: "row-reverse",
                    alignItems: "center",
                    padding: "0px",
                    "&:focus": {
                        outline: "none",
                    },
                    "&:focus-visible": {
                        outline: "3px solid #005fcc",
                        outlineOffset: "0px",
                        borderRadius: "12px",
                    },
                }}
            >
                <Box
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: "8px",
                        width: "100%",
                        paddingRight: "25px",
                    }}
                >
                    {icon && (
                        <Box
                            component="img"
                            src={icon}
                            alt=""
                            aria-hidden="true"
                            sx={{ width: 24, height: 24 }}
                        />
                    )}

                    <Typography
                        component="span"
                        sx={{
                            fontFamily: "Heebo",
                            fontWeight: 700,
                            fontSize: "16px",
                            color: "#0C3768",
                        }}
                    >
                        {title}
                    </Typography>

                    {/* ✅ כפתור חץ נגיש */}
                    <Box sx={{ marginInlineStart: "auto" }}>
                        <Box
                            component="button"
                            type="button"
                            tabIndex={-1}
                            aria-label={isExpanded ? "סגור אזור" : "פתח אזור"}
                            onClick={(e) => {
                                e.stopPropagation();
                                onClick();
                            }}
                            style={{
                                background: "transparent",
                                border: "none",
                                cursor: "pointer",
                                display: "inline-flex",
                                alignItems: "center",
                                padding: 8,
                                minWidth: 32,
                                minHeight: 32,
                                outline: "none",
                            }}
                        >
                            <Box
                                component="img"
                                src={isExpanded ? "/images/arrow_up.png" : "/images/arrow_down.png"}
                                alt=""
                                aria-hidden="true"
                                sx={{ width: 17, height: 12 }}
                            />
                        </Box>
                    </Box>
                </Box>
            </AccordionSummary>

            <AccordionDetails
                id={contentId}
                role="region"
                aria-labelledby={summaryId}
                sx={{ paddingTop: "0px" }}
            >
                {renderBody()}
            </AccordionDetails>
        </Accordion>
    );
};

export default MiddleDropdown;
